import isEmpty from 'lodash/isEmpty';

import { DEMO_GRADES_BY_SEASON } from 'a/hub/data/constants';
import { getTimestampWithMs } from 'root/lib/DatetimeManager';

import { ALLOWED_SOURCES, ALLOWED_RECEIVERS, getAppSource } from '../../lib/AppSource';
import { PRODUCT_LECTURA } from '../../lib/constants';
import { aquaAxiosWithCreds, callAsyncWithRetry } from '../../lib/utilityFunctions';
import Env from '../../lib/Env';
import { logRumError } from '../../lib/DDRum';
import { getErrorActionObject } from '../components/GlobalErrorHandler/actions';
import gameIds from '../hub/data/GameIds';
import createActionObj from '../actions/createActionObj';
import { saveApiFailures } from './apiFailuresActions';

const RUSHED_TIME = 3000;
const SECONDS_PER_QUESTION = 6500;

const RESULTS_API_URL = `${Env.API_URL}/results/v${Env.API_VERSION_KEYCLOAK}`;

export const devUpdateGameDetails = (details) => {
  const { questionCount, devDetails: { roundCompleteFlag, roundCompleteScore = 100 } = {} } = details;

  const isRushed = roundCompleteFlag === 'rushed';
  const isLowResponse = roundCompleteFlag === 'lowresponse';

  const questionsCorrect = isLowResponse || isRushed ? 0 : Math.floor(questionCount * (+roundCompleteScore / 100));
  const questionsWrong = isLowResponse ? 0 : (questionCount - questionsCorrect);

  return {
    ...details,
    questionsCorrect,
    questionsWrong,
    questionsSkippedOver: 0,
    questionsSkipped: 0,
  };
};

const devUpdateRoundData = (roundData) => {
  const {
    gameData: {
      details: {
        questionCount,
        devDetails: {
          roundCompleteFlag,
        } = {},
      },
    },
    sessionData: {
      roundStartTimestamp,
    },
  } = roundData;

  const isRushed = roundCompleteFlag === 'rushed';

  const startTime = Date.parse(roundStartTimestamp);
  const roundDuration = isRushed ? RUSHED_TIME : (questionCount * SECONDS_PER_QUESTION);
  const roundCompleteTimestamp = getTimestampWithMs(new Date(startTime + roundDuration));

  return {
    ...roundData,
    sessionData: {
      ...roundData.sessionData,
      roundCompleteTimestamp,
    },
    gameData: {
      ...roundData.gameData,
      details: devUpdateGameDetails(roundData.gameData.details),
    },
  };
};

/**
 * Validates writeResult payload before sending to the API.
 * Logs validation failures to DataDog RUM for monitoring.
 * @param {object} data writeResultConfig for /writeResult payload
 */
const validateWriteResultData = (data) => {
  const {
    GameId,
    Level,
    RoundSessionId,
    TimestampStarted,
    TimestampCompleted,
    Result: { TotalQuestions, TotalCorrect, TotalIncorrect, TotalResponses } = {},
  } = data;

  const errors = [];

  if (!RoundSessionId) {
    errors.push('RoundSessionId is missing');
  }
  if (!TimestampStarted) {
    errors.push('TimestampStarted is missing');
  }
  if (!TimestampCompleted) {
    errors.push('TimestampCompleted is missing');
  }

  if (TotalQuestions < 0) {
    errors.push('TotalQuestions is negative');
  }
  if (TotalCorrect < 0) {
    errors.push('TotalCorrect is negative');
  }
  if (TotalIncorrect < 0) {
    errors.push('TotalIncorrect is negative');
  }
  if (TotalCorrect > TotalQuestions) {
    errors.push('TotalCorrect > TotalQuestions');
  }
  if (TotalResponses !== TotalCorrect + TotalIncorrect) {
    errors.push('TotalResponses does not equal TotalCorrect + TotalIncorrect');
  }

  if (errors.length > 0) {
    const errorMessage = `writeResult validation failed for ${GameId}: ${errors.join(', ')}`;
    logRumError(
      errorMessage,
      {
        GameId,
        Level,
        RoundSessionId,
        TimestampStarted,
        TimestampCompleted,
        TotalQuestions,
        TotalCorrect,
        TotalIncorrect,
        TotalResponses,
      },
    );
    return false;
  }

  return true;
};

const recordWriteResultFailure = (roundSessionId, isResolved, writeResultData = {}) => createActionObj(
  gameIds.METAGAME,
  'SET_WRITE_RESULT_FAILURE_DATA',
  { roundSessionId, isResolved, writeResultData },
);

/**
 * Updates questWorkflowReducer to save writeResultFailures or remove it after a successful
 * resend, then persists the updated API_FAILURES gamestate bucket.
 * @param {string} roundSessionId - RoundSessionId the /writeResult PUT was made with
 * @param {boolean} isResolved - true removes the stored entry, false keeps it queued to retry
 * @param {object} writeResultData - the /writeResult PUT body that failed
 */
export const setWriteResultFailureData = (roundSessionId, isResolved, writeResultData = {}) => (dispatch) => {
  dispatch(recordWriteResultFailure(roundSessionId, isResolved, writeResultData));
  return dispatch(saveApiFailures()).catch((error) => console.error('Failed to save API_FAILURES bucket:', error));
};

// A noAsyncWithRetry error (eg a token refresh failure in getToken) throws immediately after this
// first fires with isDoneRetrying false, so it never reaches the preserve-payload branch below.
// No in-place fix: redirectToLogin has already fired by then, and saving the preserved payload would hit the identical token failure.
const logWriteResultErrors = (gameId, writeResultData, error, { timesAttempted, isDoneRetrying }) => (dispatch) => {
  const category = 'API: writeResult';
  const data = `${category} | attempt ${timesAttempted}${isDoneRetrying ? ' (final)' : ''}`;
  const metricId = 'api';
  if (!isDoneRetrying) {
    dispatch(
      getErrorActionObject(
        gameId,
        error.toString(),
        category,
        data,
        metricId,
      ),
    );
  } else {
    dispatch({
      gameId,
      type: 'PUT_WRITE_RESULTS_RESPONSE',
    });
    console.warn('you have exceeded the max amount of failed writeResult API calls');

    // preserve the payload so it can be resent on the student's next login instead of losing that data
    dispatch(setWriteResultFailureData(writeResultData.RoundSessionId, false, writeResultData));
  }
};

const logWriteResultItemsErrors = (gameId, error, { timesAttempted, isDoneRetrying }) => (dispatch) => {
  const category = 'API: writeResultItems';
  const data = `${category} | attempt ${timesAttempted}${isDoneRetrying ? ' (final)' : ''}`;
  const metricId = 'api';
  if (!isDoneRetrying) {
    dispatch(
      getErrorActionObject(
        gameId,
        error.toString(),
        category,
        data,
        metricId,
      ),
    );
  } else {
    // dispatch-only: no reducer currently handles this action type
    dispatch({
      gameId,
      type: 'POST_WRITE_RESULT_ITEMS_RESPONSE',
    });
    console.warn('you have exceeded the max amount of failed writeResultItems API calls');
  }
};

export const sendActivityUsage = async (receiver, activityUsageData) => {
  const activityUsageConfig = {
    method: 'POST',
    data: {
      thirdPartyName: receiver,
      activityUsageData,
    },
  };

  try {
    await aquaAxiosWithCreds(`${RESULTS_API_URL}/thirdParty/activityUsage`, activityUsageConfig);
  } catch (e) {
    console.warn('activityUsage request failed', e);
  }
};

const putToWriteResultApi = roundData => async (dispatch) => {
  // defaults from argument (if in dev env and has devDetails key,)
  const {
    userData: {
      userId: UserId,
      grade,
    },
    gameData: {
      gameId: GameId,
      details: {
        storyId: StoryId,
        level: Level,
        isQuestWorkflow: IsQuestWorkflow,
        isIntermediateSave: IsIntermediate,
        questionCount: TotalQuestions = 0,
        questionsCorrect: TotalCorrect = 0,
        questionsWrong: TotalIncorrect = TotalQuestions - TotalCorrect,
        questionsSkippedOver: TotalSkippedOver = 0,
        questionsSkipped = 0,
        questionsCorrectByItemType: TotalCorrectByType,
      },
    },
    sessionData,
    skillScan: {
      isAggregateResult,
      needsGrowthResult = false,
      skillScanSessionId,
      skillScanSessionData,
    },
    season,
    isDemoMode,
    product,
    skillData,
  } = (Env.isDevEnv && roundData.gameData.details.devDetails) ? devUpdateRoundData(roundData) : roundData;

  // some calculated values and other defaults
  const GameVersion = '';
  const TotalInstruction = 0;
  const TotalResponses = TotalCorrect + TotalIncorrect;
  const TotalUnanswered = TotalSkippedOver + questionsSkipped;

  const Grade = isDemoMode ? DEMO_GRADES_BY_SEASON[season] : grade;
  const {
    roundSessionId,
    roundStartTimestamp: TimestampStarted,
    roundCompleteTimestamp: TimestampCompleted,
  } = isAggregateResult ? skillScanSessionData : sessionData;
  // Cannot send 2 writeResult PUTs with same roundSessionId
  const RoundSessionId = isAggregateResult ? skillScanSessionId : roundSessionId;

  const writeResultConfig = {
    method: 'PUT',
    data: {
      UserId,
      Grade,
      Product: product,
      GameId,
      RoundSessionId,
      ...(skillScanSessionId && { SkillScanSessionId: skillScanSessionId }),
      GameVersion,
      ContentVersion: '',
      TimestampStarted,
      TimestampCompleted,
      IsQuestWorkflow,
      IsIntermediate,
      Level,
      Result: {
        StoryId,
        TotalResponses,
        TotalCorrect,
        TotalIncorrect,
        TotalQuestions,
        TotalInstruction,
        TotalSkippedOver,
        TotalUnanswered,
        ...(!isEmpty(TotalCorrectByType) && { TotalCorrectByType }),
      },
    },
  };

  // Skip the /writeResult PUT if the data itself is invalid (validateWriteResultData already logged a RUM error).
  // Still resolve the request/response cycle since retrying later won't make the payload valid.
  if (!validateWriteResultData(writeResultConfig.data)) {
    dispatch({
      gameId: GameId,
      type: 'PUT_WRITE_RESULTS_RESPONSE',
    });
    return undefined;
  }

  const appSource = getAppSource();
  const shouldSendActivityUsage = product !== PRODUCT_LECTURA && Object.values(ALLOWED_SOURCES).includes(appSource);

  dispatch({
    gameId: GameId,
    type: 'PUT_WRITE_RESULTS_REQUEST',
    payload: { product, GameId },
  });

  const asyncRetryFn = async () => {
    const { data: payload } = await aquaAxiosWithCreds(`${RESULTS_API_URL}/writeResult`, writeResultConfig);

    if (shouldSendActivityUsage) {
      const activityUsageData = {
        gameId: GameId,
        level: Level,
        skillName: skillData?.skillName || null,
      };

      await sendActivityUsage(ALLOWED_RECEIVERS[appSource], activityUsageData);
    }

    if (needsGrowthResult) {
      const endpoint = GameId.includes('mind_the_gap') ? 'mindTheGapGrowth' : 'foundationalSkillsGrowth';
      try {
        const {
          data: {
            Growth: [
              { Results } = { Results: [] },
            ],
          },
        } = await aquaAxiosWithCreds(`${RESULTS_API_URL}/${endpoint}?userIds=${UserId}&product=${product}`);
        payload.growthResult = Results.find(result => result?.Result?.SkillScanSessionId === skillScanSessionId) || {};
      } catch (e) {
        console.error(e);
        payload.growthResult = {
          RoundId: RoundSessionId,
          PercentileRank: '50.00',
        };
      }
    }
    dispatch({
      gameId: GameId,
      type: 'PUT_WRITE_RESULTS_RESPONSE',
      payload,
    });
  };

  const asyncRetryConfig = {
    logger: (...args) => dispatch(logWriteResultErrors(GameId, writeResultConfig.data, ...args)),
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

// Embedded Measurement Lambda API
const postToWriteResultItemsApi = savedItemData => async (dispatch) => {
  const { GameId } = savedItemData;
  const writeResultItemsConfig = {
    method: 'POST',
    data: savedItemData,
  };

  dispatch({
    gameId: GameId,
    type: 'POST_WRITE_RESULT_ITEMS_REQUEST',
  });

  const asyncRetryFn = async () => {
    const { data: payload } = await aquaAxiosWithCreds(`${RESULTS_API_URL}/writeResultItems`, writeResultItemsConfig);
    dispatch({
      gameId: GameId,
      type: 'POST_WRITE_RESULT_ITEMS_RESPONSE',
      payload,
    });
  };

  const asyncRetryConfig = {
    logger: (...args) => dispatch(logWriteResultItemsErrors(GameId, ...args)),
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

// helpers object for testing: https://luetkemj.github.io/170421/mocking-modules-in-jest/
export const helpers = {
  putToWriteResultApi,
  postToWriteResultItemsApi,
};

export const writeResults = (roundData, resultItemsData) => (dispatch) => new Promise((resolve) => {
  dispatch(helpers.putToWriteResultApi(roundData))
    .catch((e) => console.warn('putToWriteResultApi failed', e))
    .finally(() => {
      resolve();

      if (resultItemsData) {
        dispatch(helpers.postToWriteResultItemsApi(resultItemsData))
          .catch((e) => console.warn('postToWriteResultItemsApi failed', e));
      }
    });
});

// Statuses meaning the server permanently rejected this /writeResult payload, confirmed against aqua-api's /writeResult handler:
// 400 invalid GameId, 409 a different result already exists for this RoundSessionId, and 422 payload validation failure.
// Everything else, including 401/403 (can just mean the token needs refreshing), is transient and stays queued for the next login.
const PERMANENTLY_REJECTED_STATUSES = [400, 409, 422];

/**
 * Retries /writeResult PUTs for each failed write stored in the API_FAILURES gamestate bucket.
 * Skips the PUT_WRITE_RESULTS_REQUEST/RESPONSE dispatches, since those drive per-game reducer state
 * (eg. Skill Scan's areMeasuresCompleted/numOfStars) for the round active right now, not this replay of a past session's data.
 * Clears an entry on success, including an idempotent replay of an already-recorded result,
 * or on a permanently rejected status (see PERMANENTLY_REJECTED_STATUSES).
 * Leaves it queued to retry on the next login for any other failure.
 * @param {object} failedWriteResultData - stored failed /writeResult PUT bodies, keyed by RoundSessionId
 */
export const resendFailedWriteResults = failedWriteResultData => async (dispatch) => {
  await Promise.all(Object.entries(failedWriteResultData).map(async ([roundSessionId, data]) => {
    if (!data) {
      return;
    }
    try {
      await aquaAxiosWithCreds(`${RESULTS_API_URL}/writeResult`, { method: 'PUT', data });
      dispatch(recordWriteResultFailure(roundSessionId, true));
    } catch (error) {
      if (PERMANENTLY_REJECTED_STATUSES.includes(error?.response?.status)) {
        console.error('writeResult replay rejected, clearing stored entry:', error);
        dispatch(recordWriteResultFailure(roundSessionId, true));
      } else {
        console.error('Failed to resend writeResult:', error);
      }
    }
  }));

  return dispatch(saveApiFailures()).catch((error) => console.error('Failed to save API_FAILURES bucket:', error));
};

export default {};
